import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "../lib/i18n";

const PrivacyPolicyPage = () => {
  const { locale } = useI18n();
  const [searchParams] = useSearchParams();
  const backLink = searchParams.get("from") === "patient" ? "/bienvenue" : "/";
  const fr = locale === "fr";
  const es = locale === "es";

  const T = (frText, enText, esText) => fr ? frText : es ? (esText || enText) : enText;

  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center gap-4 mb-8">
          <Link to={backLink}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="h-8 w-auto" />
            <span className="font-heading text-lg font-semibold">Kintsugi TCC</span>
          </div>
        </div>

        <article className="prose prose-gray dark:prose-invert max-w-none">
          <h1>{T("Politique de Confidentialité", "Privacy Policy", "Política de Privacidad")}</h1>
          <p className="text-muted-foreground">{T("Dernière mise à jour : Mars 2026", "Last updated: March 2026", "Última actualización: Marzo 2026")}</p>

          <h2>{T("1. Responsable du traitement", "1. Data Controller", "1. Responsable del tratamiento")}</h2>
          <p>{T(
            "Le responsable du traitement de vos données personnelles est VMLH-TCC EURL, société inscrite au registre du commerce sous le numéro SIREN 930 612 320, dont le siège social est situé au 10 rue Antoine Chantin, 75014 Paris, France.",
            "The data controller for your personal data is VMLH-TCC EURL, a company registered under SIREN number 930 612 320, with its registered office at 10 rue Antoine Chantin, 75014 Paris, France.",
            "El responsable del tratamiento de sus datos personales es VMLH-TCC EURL, empresa registrada bajo el número SIREN 930 612 320, con sede social en 10 rue Antoine Chantin, 75014 París, Francia."
          )}</p>
          <p className="text-sm">{T(
            "Kintsugi TCC est la marque commerciale sous laquelle VMLH-TCC EURL exploite la plateforme.",
            "Kintsugi TCC is the trademark under which VMLH-TCC EURL operates the platform.",
            "Kintsugi TCC es la marca comercial bajo la cual VMLH-TCC EURL opera la plataforma."
          )}</p>

          <h2>{T("2. Données collectées", "2. Data Collected", "2. Datos recopilados")}</h2>
          <ul>
            <li>{T("Informations d'identification : nom, email", "Identity information: name, email", "Información de identificación: nombre, email")}</li>
            <li>{T("Données de santé : entrées d'humeur, notes d'exercices thérapeutiques", "Health data: mood entries, therapeutic exercise notes", "Datos de salud: entradas de estado de ánimo, notas de ejercicios terapéuticos")}</li>
            <li>{T("Données techniques : adresse IP, type de navigateur", "Technical data: IP address, browser type", "Datos técnicos: dirección IP, tipo de navegador")}</li>
            <li>{T("Données de paiement : traitées par Stripe (nous ne stockons pas vos coordonnées bancaires)", "Payment data: processed by Stripe (we do not store your bank details)", "Datos de pago: procesados por Stripe (no almacenamos sus datos bancarios)")}</li>
          </ul>

          <h2>{T("3. Finalités du traitement", "3. Purpose of Processing", "3. Finalidades del tratamiento")}</h2>
          <ul>
            <li>{T("Fourniture du service de suivi thérapeutique", "Provision of therapeutic tracking service", "Prestación del servicio de seguimiento terapéutico")}</li>
            <li>{T("Communication entre thérapeute et patient", "Communication between therapist and patient", "Comunicación entre terapeuta y paciente")}</li>
            <li>{T("Suggestions d'exercices personnalisées (IA)", "Personalized exercise suggestions (AI)", "Sugerencias de ejercicios personalizados (IA)")}</li>
            <li>{T("Gestion des abonnements et paiements", "Subscription and payment management", "Gestión de suscripciones y pagos")}</li>
          </ul>

          <h2>{T("4. Base légale", "4. Legal Basis", "4. Base legal")}</h2>
          <p>{T(
            "Le traitement de vos données de santé repose sur votre consentement explicite (article 9.2.a du RGPD). Vous pouvez retirer votre consentement à tout moment.",
            "Processing of your health data is based on your explicit consent (Article 9.2.a GDPR). You can withdraw consent at any time.",
            "El tratamiento de sus datos de salud se basa en su consentimiento explícito (artículo 9.2.a del RGPD). Puede retirar su consentimiento en cualquier momento."
          )}</p>

          <h2>{T("5. Hébergement des données de santé (HDS)", "5. Health Data Hosting (HDS)", "5. Alojamiento de datos de salud (HDS)")}</h2>
          <p>{T(
            "Conformément à la réglementation française, les données de santé des utilisateurs sont hébergées dans le respect des exigences relatives à l'Hébergement de Données de Santé (HDS). VMLH-TCC EURL s'engage à utiliser des infrastructures sécurisées et conformes aux normes en vigueur pour la protection de vos données de santé.",
            "In compliance with French regulations, user health data is hosted in accordance with Health Data Hosting (HDS) requirements. VMLH-TCC EURL is committed to using secure infrastructure compliant with current standards for the protection of your health data.",
            "De conformidad con la normativa francesa, los datos de salud de los usuarios se alojan respetando los requisitos de Alojamiento de Datos de Salud (HDS). VMLH-TCC EURL se compromete a utilizar infraestructuras seguras y conformes con las normas vigentes."
          )}</p>

          <h2>{T("6. Vos droits (RGPD)", "6. Your Rights (GDPR)", "6. Sus derechos (RGPD)")}</h2>
          <ul>
            <li>{T("Droit d'accès à vos données", "Right of access to your data", "Derecho de acceso a sus datos")}</li>
            <li>{T("Droit de rectification", "Right to rectification", "Derecho de rectificación")}</li>
            <li>{T("Droit à l'effacement (droit à l'oubli)", "Right to erasure (right to be forgotten)", "Derecho de supresión (derecho al olvido)")}</li>
            <li>{T("Droit à la portabilité des données", "Right to data portability", "Derecho a la portabilidad de los datos")}</li>
            <li>{T("Droit d'opposition et de limitation du traitement", "Right to object and restrict processing", "Derecho de oposición y limitación del tratamiento")}</li>
          </ul>
          <p>{T(
            "Vous pouvez exercer ces droits depuis votre profil (export de données, suppression de compte) ou en contactant VMLH-TCC EURL.",
            "You can exercise these rights from your profile (data export, account deletion) or by contacting VMLH-TCC EURL.",
            "Puede ejercer estos derechos desde su perfil (exportación de datos, eliminación de cuenta) o contactando a VMLH-TCC EURL."
          )}</p>

          <h2>{T("7. Conservation des données", "7. Data Retention", "7. Conservación de datos")}</h2>
          <p>{T(
            "Vos données sont conservées pendant la durée de votre utilisation du service. En cas de suppression de compte, vos données seront effacées sous 30 jours.",
            "Your data is retained for the duration of your service use. Upon account deletion, data is erased within 30 days.",
            "Sus datos se conservan durante la duración del uso del servicio. En caso de eliminación de cuenta, los datos se borran en un plazo de 30 días."
          )}</p>

          <h2>{T("8. Cookies", "8. Cookies", "8. Cookies")}</h2>
          <p>{T(
            "Nous utilisons uniquement des cookies techniques essentiels au fonctionnement du service (authentification, préférences). Aucun cookie publicitaire n'est utilisé.",
            "We only use essential technical cookies for service operation (authentication, preferences). No advertising cookies are used.",
            "Solo utilizamos cookies técnicas esenciales para el funcionamiento del servicio (autenticación, preferencias). No se utilizan cookies publicitarias."
          )}</p>

          <h2>{T("9. Contact", "9. Contact", "9. Contacto")}</h2>
          <p>{T(
            "Pour toute question relative à la protection de vos données, contactez :",
            "For any questions regarding your data protection, contact:",
            "Para cualquier pregunta sobre la protección de sus datos, contacte:"
          )}</p>
          <ul className="list-none pl-0">
            <li><strong>VMLH-TCC EURL</strong></li>
            <li>SIREN : 930 612 320</li>
            <li>10 rue Antoine Chantin, 75014 Paris</li>
            <li>Email : contact@kintsugi-tcc.fr</li>
          </ul>
        </article>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
